import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import { Children, cloneElement, memo, useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { TransitionGroup } from 'react-transition-group';
import styleUtils from '../../styles/styleUtils';
import theme from '../../styles/theme';
import { FadeReveal } from '../FadeReveal/index';
import { Icon } from '../Icon/index';
import { Reveal } from '../Reveal/index';
import { borderTypes, sizes, } from '../Select/constants';
import { errorMessageStyles, useErrorSpacingStyles, useIconContainerStyles, useInnerLabelStyles, useSelectContainerStyles, useSelectStyles, } from '../Select/styles';
import { SelectCustomContext } from './SelectCustomContext';
import { getDisplayName } from './utils';
const dropDownStyles = css `
  background-color: ${theme.colors.gray100};
  box-shadow: 0 0 ${styleUtils.rem(6)} 0 rgba(0, 0, 0, 0.3);
  list-style: none;
  margin: 0;
  max-height: ${styleUtils.rem(300)};
  overflow-y: scroll;
  padding-bottom: 0;
  padding-left: 0;
  padding-right: 0;
  padding-top: 0;
  position: absolute;
  width: 100%;
  z-index: 9999;
`;
const defaultProps = {
    borderType: borderTypes.full,
    disabled: false,
    error: '',
    hasErrorSpacing: false,
    id: '',
    onBlur: () => { },
    placeholder: 'Select One',
    selectedOption: {
        text: '',
        value: '',
    },
    size: sizes.medium,
    touched: false,
};
const SelectCustom = ({ borderType = defaultProps.borderType, children, disabled = defaultProps.disabled, error = defaultProps.error, hasErrorSpacing = defaultProps.hasErrorSpacing, id, label, name, onBlur = defaultProps.onBlur, onChange, placeholder = defaultProps.placeholder, selectedOption = defaultProps.selectedOption, size = defaultProps.size, touched = defaultProps.touched, }) => {
    const containerRef = useRef(null);
    const buttonRef = useRef(null);
    const [isDropdownVisible, setIsDropdownVisible,] = useState(false);
    const inputId = useMemo(() => (id || name), [
        id,
        name,
    ]);
    const toggleDropdownVisibility = useCallback(() => (setIsDropdownVisible((currentState) => (!currentState))), []);
    const providerValue = useMemo(() => ({
        onChange: (newValue) => {
            var _a;
            toggleDropdownVisibility();
            (_a = buttonRef === null || buttonRef === void 0 ? void 0 : buttonRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            onChange(newValue);
        },
        selectedValue: selectedOption.value,
    }), [
        onChange,
        selectedOption,
        toggleDropdownVisibility,
    ]);
    const onUnrelatedClick = useCallback((event) => {
        if (containerRef.current
            && !containerRef.current.contains(event.target)) {
            setIsDropdownVisible(false);
        }
    }, []);
    useEffect(() => {
        if (!isDropdownVisible) {
            return () => { };
        }
        document.addEventListener('click', onUnrelatedClick, false);
        return () => {
            document.removeEventListener('click', onUnrelatedClick, false);
        };
    }, [
        isDropdownVisible,
        onUnrelatedClick,
    ]);
    const options = useMemo(() => (Children
        .toArray(children)
        .filter((child) => ([
        'SelectCustomOption',
        'SelectCustomOptionGroup',
    ]
        .includes(getDisplayName(child))))
        .map((child, index, filteredChildren) => (cloneElement(child, getDisplayName(child) === 'SelectCustomOptionGroup'
        ? Object.assign({ breakBottom: ((index !== filteredChildren.length - 1)
                && getDisplayName(filteredChildren[index + 1]) === 'SelectCustomOption'), breakTop: (index !== 0) }, child.props) : {})))), [
        children,
    ]);
    const errorSpacingStyles = useErrorSpacingStyles({
        size,
    });
    const innerLabelStyles = useInnerLabelStyles({
        size,
    });
    const selectContainerStyles = useSelectContainerStyles({
        disabled,
        size,
    });
    const wrapperStyles = useMemo(() => (css `
        position: relative;
        width: 100%;
        ${hasErrorSpacing && errorSpacingStyles}
      `), [
        errorSpacingStyles,
        hasErrorSpacing,
    ]);
    const buttonStyles = useSelectStyles({
        borderType,
        disabled,
        error,
        isDefaultFont: !selectedOption.option,
        label,
        size,
        touched,
        value: selectedOption.value,
    });
    const iconContainerStyles = useIconContainerStyles({
        size,
    });
    return (_jsx("div", Object.assign({ ref: containerRef }, { children: _jsxs("div", Object.assign({ css: wrapperStyles }, { children: [_jsxs("div", Object.assign({ css: selectContainerStyles }, { children: [label && (_jsx("label", Object.assign({ css: innerLabelStyles, htmlFor: inputId }, { children: label }))), _jsx("button", Object.assign({ "aria-haspopup": "listbox", css: buttonStyles, disabled: disabled, id: inputId, onBlur: onBlur, onClick: toggleDropdownVisibility, ref: buttonRef, tabIndex: 0, type: "button" }, { children: selectedOption.option
                                || selectedOption.text
                                || placeholder })), _jsx("div", Object.assign({ css: iconContainerStyles }, { children: _jsx(Icon, { size: "extraSmall", type: "arrow-down" }) }))] })), _jsx(TransitionGroup, { children: isDropdownVisible && (_jsx(Reveal, Object.assign({ in: isDropdownVisible }, { children: _jsx("ul", Object.assign({ css: dropDownStyles, role: "listbox" }, { children: _jsx(SelectCustomContext.Provider, Object.assign({ value: providerValue }, { children: options })) })) }))) }), _jsx(FadeReveal, Object.assign({ in: Boolean(touched) && Boolean(error), unmountOnExit: true }, { children: _jsx("span", Object.assign({ css: errorMessageStyles, role: "alert" }, { children: error })) }))] })) })));
};
const MemoizedSelectCustom = memo(SelectCustom);
MemoizedSelectCustom.displayName = 'SelectCustom';
export { MemoizedSelectCustom as SelectCustom };
// MC-1068 Temporary hack for Storybook.
export default MemoizedSelectCustom;
